import { ref, computed, onMounted, onUnmounted } from 'vue'
import { toCurrencyString, formatNumber } from '@/utils/formatters'
import flashMessageService from '@/services/flashMessageService'

export function useCarCard(props, emit) {
  // Reactive state
  const currentIndex = ref(0)
  const activeTooltip = ref(null)
  const isModalVisible = ref(false)
  const touchStartX = ref(0)
  const touchStartY = ref(0)
  const touchEndX = ref(0)
  const isDragging = ref(false)
  const dragOffset = ref(0)

  // Computed properties
  const images = computed(() => props.offer.car.images || [])

  // Helper functions to get previous and next indices
  function getPrevIndex() {
    return (currentIndex.value + images.value.length - 1) % images.value.length
  }

  function getNextIndex() {
    return (currentIndex.value + 1) % images.value.length
  }

  // Image navigation functions
  function next() {
    currentIndex.value = getNextIndex()
  }

  function prev() {
    currentIndex.value = getPrevIndex()
  }

  // Touch/swipe handling functions
  function handleTouchStart(e) {
    // Only process if we have multiple images
    if (images.value.length > 1) {
      touchStartX.value = e.touches[0].clientX
      touchStartY.value = e.touches[0].clientY
      dragOffset.value = 0
    }
  }

  function handleTouchMove(e) {
    // Only process if we have multiple images
    if (images.value.length > 1) {
      // Get the touch coordinates
      const touchX = e.touches[0].clientX
      const touchY = e.touches[0].clientY

      // Calculate horizontal and vertical movement
      const rawOffsetX = touchX - touchStartX.value
      const rawOffsetY = touchY - touchStartY.value

      // Check if the swipe is more horizontal than vertical
      const deltaX = Math.abs(rawOffsetX)
      const deltaY = Math.abs(rawOffsetY)

      // Only prevent default (page scrolling) if the movement is primarily horizontal
      if (deltaX > deltaY) {
        e.preventDefault() // Prevent page scrolling only during horizontal swipe

        // Get container width for calculations
        const containerWidth = document.querySelector('.carousel-container')?.offsetWidth || window.innerWidth
        // Allow for full visibility of next/previous images during swiping (100% of the container width)
        const maxOffset = containerWidth
        dragOffset.value = Math.max(Math.min(rawOffsetX, maxOffset), -maxOffset)
      }
    }
  }

  function handleTouchEnd(e) {
    // Only process if we have multiple images
    if (images.value.length > 1) {
      touchEndX.value = e.changedTouches[0].clientX
      const swipeDetected = handleSwipe()

      if (swipeDetected) {
        // Only prevent default behavior if a swipe was detected and handled
        e.stopPropagation() // Prevent event from bubbling up to parent link
        e.preventDefault() // Prevent default link behavior
      }

      dragOffset.value = 0
    }
  }

  // Mouse event handlers for desktop devices
  function handleMouseDown(e) {
    // Only process if we have multiple images
    if (images.value.length > 1) {
      isDragging.value = true
      touchStartX.value = e.clientX
      touchStartY.value = e.clientY
      dragOffset.value = 0
      e.preventDefault() // Prevent text selection during drag
    }
  }

  function handleMouseMove(e) {
    if (isDragging.value) {
      // Calculate horizontal and vertical movement
      const rawOffsetX = e.clientX - touchStartX.value
      const rawOffsetY = e.clientY - touchStartY.value

      // Check if the movement is more horizontal than vertical
      const deltaX = Math.abs(rawOffsetX)
      const deltaY = Math.abs(rawOffsetY)

      // Only prevent default and handle drag if movement is primarily horizontal
      if (deltaX > deltaY) {
        e.preventDefault() // Prevent text selection during drag

        // Get container width for calculations
        const containerWidth = document.querySelector('.carousel-container')?.offsetWidth || window.innerWidth
        // Allow for full visibility of next/previous images during swiping (100% of the container width)
        const maxOffset = containerWidth
        dragOffset.value = Math.max(Math.min(rawOffsetX, maxOffset), -maxOffset)
      }
    }
  }

  function handleMouseUp(e) {
    if (isDragging.value && images.value.length > 1) {
      isDragging.value = false
      touchEndX.value = e.clientX
      const swipeDetected = handleSwipe()

      if (swipeDetected) {
        // Only prevent default behavior if a swipe was detected and handled
        e.stopPropagation() // Prevent event from bubbling up to parent link
        e.preventDefault() // Prevent default link behavior
      }

      dragOffset.value = 0
    }
  }

  function handleMouseLeave(e) {
    if (isDragging.value && images.value.length > 1) {
      isDragging.value = false
      touchEndX.value = e.clientX
      const swipeDetected = handleSwipe()

      if (swipeDetected) {
        // Only prevent default behavior if a swipe was detected and handled
        e.stopPropagation() // Prevent event from bubbling up to parent link
        e.preventDefault() // Prevent default link behavior
      }

      dragOffset.value = 0
    }
  }

  function handleSwipe() {
    const containerWidth = document.querySelector('.carousel-container')?.offsetWidth || window.innerWidth
    const swipeThreshold = containerWidth * 0.15 // 15% of container width
    const swipeDistance = touchEndX.value - touchStartX.value

    if (Math.abs(swipeDistance) >= swipeThreshold) {
      // If we detected a valid swipe, prevent navigation to detail page
      if (swipeDistance > 0) {
        // Swipe right - go to previous image
        prev()
      } else {
        // Swipe left - go to next image
        next()
      }
      return true; // Indicate that a swipe was detected and handled
    }
    return false; // No swipe detected
  }

  // Badge computation
  const badges = computed(() => {
    const b = []

    if (props.offer.availability_type) {
      // Map availability type slug to the correct badge class, similar to getBadgeClass() method
      let badgeClass = 'badge-new';
      const availabilityId = props.offer.availability_type.id;

      if (availabilityId === 2) {
        badgeClass = 'badge-used';
      } else if (availabilityId === 3) {
        badgeClass = 'badge-demo';
      } else if (availabilityId === 4) {
        badgeClass = 'badge-ol';
      }

      b.push({
        text: props.offer.availability_type.name,
        class: badgeClass
      })
    }

    ;(props.offer.car.tags || []).forEach(tag => {
      // Check if the tag has a slug property
      if (!tag.slug) {
        return
      }

      // Check if the tag slug is one of the special ones or contains similar text
      const specialSlugs = ['volvo-select', 'in-stock', 'soon-in-stock', 'approved']
      const isExactMatch = specialSlugs.includes(tag.slug)

      // Also check for partial matches (case insensitive)
      const isPartialMatch = !isExactMatch && (
        tag.slug.toLowerCase().includes('stock') ||
        tag.slug.toLowerCase().includes('skladem') ||
        tag.slug.toLowerCase().includes('soon') ||
        tag.slug.toLowerCase().includes('brzy') ||
        tag.slug.toLowerCase().includes('volvo') && tag.slug.toLowerCase().includes('select')
      )

      if (isExactMatch || isPartialMatch) {
        // Determine which special tag this is
        let specialType = ''
        if (tag.slug.toLowerCase().includes('soon') || tag.slug.toLowerCase().includes('brzy')) {
          specialType = 'soon-in-stock'
        } else if (tag.slug.toLowerCase().includes('stock') || tag.slug.toLowerCase().includes('skladem')) {
          specialType = 'in-stock'
        } else if (tag.slug.toLowerCase().includes('volvo') && tag.slug.toLowerCase().includes('select')) {
          specialType = 'volvo-select'
        } else if (tag.slug.toLowerCase().includes('approved')) {
          specialType = 'approved'
        } else {
          specialType = tag.slug // fallback to original slug
        }
        // Use translation if available, otherwise fallback to tag title
        // First try to get translation for the standardized type, then for the original slug
        const text = props.badgeTranslations[specialType] ||
                     props.badgeTranslations[tag.slug] ||
                     tag.title

        // Map volvo-select to badge--select, otherwise use badge--{standardized type}
        const badgeClass = specialType === 'volvo-select' ? 'badge--select' : `badge--${specialType}`

        b.push({ text, class: badgeClass })
      }
    })

    // Use translations if available, otherwise fallback to hardcoded values
    if (props.offer.car.status === 'reserved') {
      b.push({
        text: props.badgeTranslations['reserved'],
        class: 'badge--reserved'
      })
    }

    if (props.offer.car.is_action) {
      b.push({
        text: props.badgeTranslations['action'],
        class: 'badge--action'
      })
    }

    if (props.offer.car.status === 'sold') {
      b.push({
        text: props.badgeTranslations['sold'],
        class: 'badge--sold'
      })
    }

    return b
  })

  // Custom tags (prefer new relation, fallback to legacy type)
  const customTags = computed(() => {
    const relationalTags = Array.isArray(props.offer.car.custom_tags)
      ? props.offer.car.custom_tags.filter(tag => tag && tag.title)
      : []

    const source = relationalTags.length
      ? relationalTags.map(tag => ({
          keyBase: `custom-${tag.id ?? tag.slug ?? tag.title}`,
          title: tag.title,
        }))
      : (props.offer.car.tags || [])
          .filter(tag => tag && tag.type === 'custom' && tag.title)
          .map(tag => ({
            keyBase: `legacy-${tag.id ?? tag.slug ?? tag.title}`,
            title: tag.title,
          }))

    return source.map((tag, index) => ({
      key: `${tag.keyBase}-${index}`,
      title: tag.title,
      classes: ['badge--custom'],
      style: index === 1 ? { background: '#00C8FF' } : null,
    }))
  })

  // Price formatting
  const formattedMileage = computed(
    () => formatNumber(props.offer.car.current_mileage || 0) + ' km'
  )
  
  const currencyCode = computed(() => props.currencyCode || 'CZK')

  const priceInclVat = computed(() =>
    toCurrencyString(props.offer.price, currencyCode.value)
  );

  const priceExclVat = computed(() =>
    props.offer.price_excl_vat && props.offer.vat_deduction_allowed
      ? toCurrencyString(props.offer.price_excl_vat, currencyCode.value)
      : null
  );

  const monthlyInclVat = computed(() =>
    props.offer.min_monthly_installment
      ? toCurrencyString(props.offer.min_monthly_installment, currencyCode.value)
      : null
  );

  const monthlyExclVat = computed(() =>
    props.offer.min_monthly_installment_excl_vat && props.offer.vat_deduction_allowed
      ? toCurrencyString(props.offer.min_monthly_installment_excl_vat, currencyCode.value)
      : null
  );

  // Action functions
  function toggleCompare() {
    const comparisonPayload = {
      id: props.offer.id,
      offer: props.offer,
    }

    if (props.isCompared) {
      emit('remove-from-comparison', comparisonPayload)
      flashMessageService.showSuccess(props.lblRemovedFromComparison || props.lblRemoveFromComparison)
    } else {
      try {
        emit('add-to-comparison', comparisonPayload)
        flashMessageService.showSuccess(props.lblAddedToComparison || props.lblAddToComparison)
      } catch (error) {
        // If there's an error (like comparison limit reached), show a danger message
        flashMessageService.showDanger(props.lblComparedCarsLimitReached || error.message || 'Něco se pokazilo. Zkuste to prosím znovu.')
      }
    }
  }

  function toggleFavorite() {
    const favoritePayload = {
      id: props.offer.id,
      offer: props.offer,
    }

    if (props.isFavorite) {
      emit('remove-from-favorites', favoritePayload)
      flashMessageService.showSuccess(props.lblRemovedFromFavorites || props.lblRemoveFromFavorites)
    } else {
      emit('add-to-favorites', favoritePayload)
      flashMessageService.showSuccess(props.lblAddedToFavorites || props.lblAddToFavorites)
    }
  }

  // Link sharing
  function copyLink() {
    // Check if navigator.clipboard is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(props.offer.detail_link)
        .then(() => {
          flashMessageService.showSuccess(props.lblShare + ' - odkaz zkopírován do schránky')
        })
        .catch(err => {
          fallbackCopyTextToClipboard(props.offer.detail_link)
        })
    } else {
      // Fallback for browsers that don't support clipboard API
      fallbackCopyTextToClipboard(props.offer.detail_link)
    }
  }

  // Fallback method using document.execCommand
  function fallbackCopyTextToClipboard(text) {
    try {
      const textArea = document.createElement('textarea')
      textArea.value = text

      // Make the textarea out of viewport
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)

      // Select and copy
      textArea.focus()
      textArea.select()
      const successful = document.execCommand('copy')

      // Clean up
      document.body.removeChild(textArea)

      if (successful) {
        flashMessageService.showSuccess(props.lblShare + ' - odkaz zkopírován do schránky')
      } else {
        flashMessageService.showDanger('Nepodařilo se zkopírovat odkaz do schránky')
      }
    } catch (err) {
      flashMessageService.showDanger('Nepodařilo se zkopírovat odkaz do schránky')
    }
  }

  // Navigation to details
  function goToDetails() {
    emit('car-selected', props.offer)
    window.location.href = props.offer.detail_link
  }

  // Volvo Select tooltip
  function showVolvoSelectTooltip(event) {
    // Check if we're on mobile
    if (window.innerWidth <= 768) {
      // Show modal on mobile
      isModalVisible.value = true;
    } else {
      // Show tooltip on desktop
      // Get the trigger element
      const trigger = event.currentTarget;

      // Close any other active tooltips
      document.querySelectorAll('.volvo-selekt-tooltip-trigger.active').forEach((activeTrigger) => {
        if (activeTrigger !== trigger) {
          activeTrigger.classList.remove('active');
        }
      });

      // Toggle the current one
      trigger.classList.toggle('active');
    }

    // Prevent the event from propagating to the document click handler
    event.stopPropagation();
  }

  // Close tooltip when clicking outside
  function handleDocumentClick(e) {
    const activeTriggers = document.querySelectorAll('.volvo-selekt-tooltip-trigger.active');
    if (!Array.from(activeTriggers).some(trigger => trigger.contains(e.target))) {
      activeTriggers.forEach(trigger => trigger.classList.remove('active'));
    }
  }

  // Global mouse event handlers for drag outside carousel
  function handleGlobalMouseUp(e) {
    if (isDragging.value) {
      handleMouseUp(e)
    }
  }

  function handleGlobalMouseMove(e) {
    if (isDragging.value) {
      // Calculate horizontal and vertical movement
      const rawOffsetX = e.clientX - touchStartX.value
      const rawOffsetY = e.clientY - touchStartY.value

      // Check if the movement is more horizontal than vertical
      const deltaX = Math.abs(rawOffsetX)
      const deltaY = Math.abs(rawOffsetY)

      // Only handle drag if movement is primarily horizontal
      if (deltaX > deltaY) {
        // Get container width for calculations
        const containerWidth = document.querySelector('.carousel-container')?.offsetWidth || window.innerWidth
        // Allow for full visibility of next/previous images during swiping (100% of the container width)
        const maxOffset = containerWidth
        dragOffset.value = Math.max(Math.min(rawOffsetX, maxOffset), -maxOffset)
      }
    }
  }

  // Lifecycle hooks
  onMounted(() => {
    document.addEventListener('click', handleDocumentClick)
    document.addEventListener('mouseup', handleGlobalMouseUp)
    document.addEventListener('mousemove', handleGlobalMouseMove)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleDocumentClick)
    document.removeEventListener('mouseup', handleGlobalMouseUp)
    document.removeEventListener('mousemove', handleGlobalMouseMove)
  })

  // Return all the reactive state and methods
  return {
    // State
    currentIndex,
    activeTooltip,
    isModalVisible,
    touchStartX,
    touchStartY,
    touchEndX,
    isDragging,
    dragOffset,

    // Computed
    images,
    badges,
    customTags,
    formattedMileage,
    priceInclVat,
    priceExclVat,
    monthlyInclVat,
    monthlyExclVat,

    // Methods
    next,
    prev,
    getPrevIndex,
    getNextIndex,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleSwipe,
    toggleCompare,
    toggleFavorite,
    copyLink,
    fallbackCopyTextToClipboard,
    goToDetails,
    showVolvoSelectTooltip,
    handleDocumentClick,
  }
}
